import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  InputAdornment,
  Slider,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as InvoiceIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNotify } from 'react-admin';
import { useBrand } from './ServerBrandProvider';

interface EstimateData {
  id: string;
  estimateNumber: string;
  customerId: string;
  customer?: {
    id: string;
    displayName: string;
    email?: string;
  };
  title: string;
  totalAmount: number;
  subtotal: number;
  taxAmount: number;
  taxRate: number;
  lineItems: Array<{
    name: string;
    description: string;
    quantity: number;
    unitPrice: number;
  }>;
}

interface InvoiceToCreate {
  id: string;
  title: string;
  amount: number;
  dueDate: Date;
  description: string;
  milestoneId?: string;
}

type InvoiceType = 'single' | 'downpayment' | 'monthly' | 'custom' | 'milestone';

interface InvoiceGenerationWizardProps {
  estimate: EstimateData;
  onGenerate: (invoices: InvoiceToCreate[]) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

const steps = ['Invoice Type', 'Configuration', 'Review & Generate'];

export const InvoiceGenerationWizard: React.FC<InvoiceGenerationWizardProps> = ({
  estimate,
  onGenerate,
  onClose,
  isLoading = false,
}) => {
  const notify = useNotify();
  const { brand } = useBrand();
  const [activeStep, setActiveStep] = useState(0);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('single');
  const [invoicesToCreate, setInvoicesToCreate] = useState<InvoiceToCreate[]>([]);

  // Configuration state
  const [downPaymentPercent, setDownPaymentPercent] = useState(50);
  const [monthlyInstallments, setMonthlyInstallments] = useState(3);
  const [startDate, setStartDate] = useState(new Date());
  const [customInvoices, setCustomInvoices] = useState<InvoiceToCreate[]>([]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const generateInvoicesPreview = () => {
    const invoices: InvoiceToCreate[] = [];
    const baseDate = new Date(startDate);

    const invoiceTitle = estimate.title || `Estimate ${estimate.estimateNumber}`;

    switch (invoiceType) {
      case 'single':
        invoices.push({
          id: '1',
          title: `Invoice for ${invoiceTitle}`,
          amount: estimate.totalAmount,
          dueDate: new Date(baseDate.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days
          description: `Full payment for estimate ${estimate.estimateNumber}`,
        });
        break;

      case 'downpayment':
        const downPaymentAmount = (estimate.totalAmount * downPaymentPercent) / 100;
        const remainingAmount = estimate.totalAmount - downPaymentAmount;

        invoices.push({
          id: '1',
          title: `Down Payment - ${invoiceTitle}`,
          amount: downPaymentAmount,
          dueDate: new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000), // 7 days
          description: `Down payment (${downPaymentPercent}%) for ${estimate.estimateNumber}`,
        });

        invoices.push({
          id: '2',
          title: `Final Payment - ${invoiceTitle}`,
          amount: remainingAmount,
          dueDate: new Date(baseDate.getTime() + 60 * 24 * 60 * 60 * 1000), // 60 days
          description: `Final payment for ${estimate.estimateNumber}`,
        });
        break;

      case 'monthly':
        const monthlyAmount = estimate.totalAmount / monthlyInstallments;
        for (let i = 0; i < monthlyInstallments; i++) {
          const dueDate = new Date(baseDate);
          dueDate.setMonth(dueDate.getMonth() + i);

          invoices.push({
            id: (i + 1).toString(),
            title: `${invoiceTitle} - Payment ${i + 1}/${monthlyInstallments}`,
            amount:
              i === monthlyInstallments - 1
                ? estimate.totalAmount - monthlyAmount * (monthlyInstallments - 1) // Last payment gets remainder
                : monthlyAmount,
            dueDate,
            description: `Monthly payment ${i + 1} of ${monthlyInstallments} for ${
              estimate.estimateNumber
            }`,
          });
        }
        break;

      case 'custom':
        return customInvoices;

      case 'milestone':
        // Default milestones - user can customize
        const milestones = [
          { name: 'Project Start', percent: 30 },
          { name: 'Midpoint', percent: 40 },
          { name: 'Completion', percent: 30 },
        ];

        milestones.forEach((milestone, i) => {
          const amount = (estimate.totalAmount * milestone.percent) / 100;
          const dueDate = new Date(baseDate);
          dueDate.setDate(dueDate.getDate() + (i + 1) * 30); // 30 days apart

          invoices.push({
            id: (i + 1).toString(),
            title: `${milestone.name} - ${invoiceTitle}`,
            amount,
            dueDate,
            description: `${milestone.name} payment for ${estimate.estimateNumber}`,
            milestoneId: milestone.name.toLowerCase().replace(' ', '_'),
          });
        });
        break;
    }

    return invoices;
  };

  useEffect(() => {
    setInvoicesToCreate(generateInvoicesPreview());
  }, [invoiceType, downPaymentPercent, monthlyInstallments, startDate, customInvoices, estimate]);

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate invoice type selection
      if (!invoiceType) {
        notify('Please select an invoice type', { type: 'warning' });
        return;
      }
    }

    if (activeStep === 1) {
      // Validate configuration
      if (invoiceType === 'custom' && customInvoices.length === 0) {
        notify('Please add at least one custom invoice', { type: 'warning' });
        return;
      }
    }

    if (activeStep === steps.length - 1) {
      // Generate invoices
      handleGenerate();
      return;
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleGenerate = async () => {
    try {
      await onGenerate(invoicesToCreate);
      notify(`Generated ${invoicesToCreate.length} invoice(s) successfully!`, { type: 'success' });
      onClose();
    } catch (error) {
      notify('Failed to generate invoices', { type: 'error' });
    }
  };

  const addCustomInvoice = () => {
    const newInvoice: InvoiceToCreate = {
      id: Date.now().toString(),
      title: `Custom Invoice ${customInvoices.length + 1}`,
      amount: 0,
      dueDate: new Date(),
      description: '',
    };
    setCustomInvoices([...customInvoices, newInvoice]);
  };

  const updateCustomInvoice = (id: string, updates: Partial<InvoiceToCreate>) => {
    setCustomInvoices(prev =>
      prev.map(invoice => (invoice.id === id ? { ...invoice, ...updates } : invoice))
    );
  };

  const removeCustomInvoice = (id: string) => {
    setCustomInvoices(prev => prev.filter(invoice => invoice.id !== id));
  };

  const renderStep1 = () => (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <InvoiceIcon color="primary" />
          Select Invoice Type
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Choose how you want to split the estimate total of{' '}
            <strong>{formatCurrency(estimate.totalAmount)}</strong> into invoices.
          </Typography>
        </Alert>

        <FormControl component="fieldset">
          <RadioGroup
            value={invoiceType}
            onChange={e => setInvoiceType(e.target.value as InvoiceType)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    border: invoiceType === 'single' ? '2px solid' : '1px solid',
                    borderColor: invoiceType === 'single' ? 'primary.main' : 'divider',
                  }}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6">Single Invoice</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Generate one invoice for the full amount
                        </Typography>
                        <Chip
                          label={formatCurrency(estimate.totalAmount)}
                          color="primary"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    }
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    border: invoiceType === 'downpayment' ? '2px solid' : '1px solid',
                    borderColor: invoiceType === 'downpayment' ? 'primary.main' : 'divider',
                  }}
                >
                  <FormControlLabel
                    value="downpayment"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6">Down Payment + Final</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Split into down payment and final payment
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip label="Down Payment" color="info" size="small" />
                          <Chip label="Final Payment" color="success" size="small" />
                        </Stack>
                      </Box>
                    }
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    border: invoiceType === 'monthly' ? '2px solid' : '1px solid',
                    borderColor: invoiceType === 'monthly' ? 'primary.main' : 'divider',
                  }}
                >
                  <FormControlLabel
                    value="monthly"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6">Monthly Installments</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Split into equal monthly payments
                        </Typography>
                        <Chip label="Equal Payments" color="warning" size="small" sx={{ mt: 1 }} />
                      </Box>
                    }
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    border: invoiceType === 'milestone' ? '2px solid' : '1px solid',
                    borderColor: invoiceType === 'milestone' ? 'primary.main' : 'divider',
                  }}
                >
                  <FormControlLabel
                    value="milestone"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6">Milestone-Based</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Invoices tied to project milestones
                        </Typography>
                        <Chip
                          label="Project Phases"
                          color="secondary"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    }
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    border: invoiceType === 'custom' ? '2px solid' : '1px solid',
                    borderColor: invoiceType === 'custom' ? 'primary.main' : 'divider',
                  }}
                >
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6">Custom Split</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Manually define invoice amounts and dates
                        </Typography>
                        <Chip
                          label="Maximum Flexibility"
                          color="default"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    }
                  />
                </Paper>
              </Grid>
            </Grid>
          </RadioGroup>
        </FormControl>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <MoneyIcon color="primary" />
          Configure Invoice Details
        </Typography>

        {invoiceType === 'single' && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                A single invoice will be generated for the full amount of{' '}
                {formatCurrency(estimate.totalAmount)}.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Due Date"
                  value={startDate}
                  onChange={date => date && setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {invoiceType === 'downpayment' && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Down Payment Percentage
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={downPaymentPercent}
                    onChange={(_, value) => setDownPaymentPercent(value as number)}
                    min={10}
                    max={90}
                    step={5}
                    marks={[
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                    ]}
                    valueLabelDisplay="on"
                    valueLabelFormat={value => `${value}%`}
                  />
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Down Payment
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        {formatCurrency((estimate.totalAmount * downPaymentPercent) / 100)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Final Payment
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(
                          estimate.totalAmount - (estimate.totalAmount * downPaymentPercent) / 100
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={date => date && setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Down payment due date will be 7 days from this date',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {invoiceType === 'monthly' && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Monthly Payments"
                  value={monthlyInstallments}
                  onChange={e =>
                    setMonthlyInstallments(Math.max(2, parseInt(e.target.value, 10) || 2))
                  }
                  inputProps={{ min: 2, max: 12 }}
                  helperText="Between 2 and 12 months"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="First Payment Date"
                  value={startDate}
                  onChange={date => date && setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Subsequent payments will be monthly from this date',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Monthly Payment Amount
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    ~{formatCurrency(estimate.totalAmount / monthlyInstallments)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Last payment may vary slightly)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {invoiceType === 'custom' && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1">Custom Invoices</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCustomInvoice}
                size="small"
              >
                Add Invoice
              </Button>
            </Stack>

            {customInvoices.length === 0 ? (
              <Alert severity="info">
                Click &quot;Add Invoice&quot; to create custom invoices with your own amounts and
                due dates.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customInvoices.map(invoice => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={invoice.title}
                            onChange={e =>
                              updateCustomInvoice(invoice.id, { title: e.target.value })
                            }
                            placeholder="Invoice title"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={invoice.amount}
                            onChange={e =>
                              updateCustomInvoice(invoice.id, {
                                amount: parseFloat(e.target.value) || 0,
                              })
                            }
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            value={invoice.dueDate}
                            onChange={date =>
                              date && updateCustomInvoice(invoice.id, { dueDate: date })
                            }
                            slotProps={{
                              textField: { size: 'small' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={invoice.description}
                            onChange={e =>
                              updateCustomInvoice(invoice.id, { description: e.target.value })
                            }
                            placeholder="Description"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeCustomInvoice(invoice.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {customInvoices.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={
                    customInvoices.reduce((sum, inv) => sum + inv.amount, 0) ===
                    estimate.totalAmount
                      ? 'success'
                      : 'warning'
                  }
                >
                  <Typography variant="body2">
                    Total custom invoice amount:{' '}
                    {formatCurrency(customInvoices.reduce((sum, inv) => sum + inv.amount, 0))}
                    <br />
                    Estimate total: {formatCurrency(estimate.totalAmount)}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {invoiceType === 'milestone' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Default milestone structure is provided. You can customize amounts and dates as
                needed.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Project Start Date"
                  value={startDate}
                  onChange={date => date && setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Milestones will be spaced 30 days apart',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <CheckIcon color="success" />
            Review Invoices
          </Typography>

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {invoicesToCreate.length} invoice(s) will be generated for a total of{' '}
              <strong>
                {formatCurrency(invoicesToCreate.reduce((sum, inv) => sum + inv.amount, 0))}
              </strong>
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Invoice</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoicesToCreate.map((invoice, _index) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {invoice.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      >
                        {formatCurrency(invoice.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.dueDate.toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {invoice.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Estimate Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Estimate:</strong> {estimate.estimateNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {estimate.customer?.displayName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Title:</strong> {estimate.title}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {formatCurrency(estimate.totalAmount)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CardContent>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
            Generate Invoices
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}
          >
            From Estimate: {estimate.estimateNumber} - {formatCurrency(estimate.totalAmount)}
          </Typography>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      {/* Navigation */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={activeStep === 0 ? onClose : handleBack}
              startIcon={activeStep === 0 ? undefined : <BackIcon />}
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <NextIcon />}
              disabled={isLoading}
              sx={{
                background:
                  brand?.theme?.buttonGradient ||
                  'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                color: 'white',
                '&:hover': {
                  background:
                    brand?.theme?.buttonGradient ||
                    'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  opacity: 0.9,
                },
              }}
            >
              {activeStep === steps.length - 1
                ? `Generate ${invoicesToCreate.length} Invoice(s)`
                : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
